/** Vision cone, sound cone, and Bresenham line-of-sight detection for enemy AI. */
import type { EnemyArchetypeConfig, PlayerHideState } from '../utils/Types';
import { DETECTION_RANGE } from '../utils/Constants';
import { normalize, worldToTile, degreesToRadians, magnitude, dot, distanceSq } from '../utils/Math';
import type { AIState } from './constants';

/** Within 3 tiles, LOS always succeeds — can't lose sight of someone at arm's reach. */
const POINT_BLANK_RANGE_SQ = 48 * 48;

/** Check if a tile blocks line-of-sight. Null tiles and colliding non-transparent tiles block. */
export function tileBlocksLOS(tile: Phaser.Tilemaps.Tile | null): boolean {
  if (!tile) return true;
  return tile.collides && !tile.properties?.losTransparent;
}

/**
 * Handles vision cones, sound cones, Bresenham LOS raycast, and player tracking.
 * Stateless per-frame — call update() each frame, read results via getters.
 */
export class Detection {
  // Precomputed cone geometry
  readonly effectiveDetectionRadius: number;
  readonly effectiveDetectionRadiusSq: number;
  private readonly visionAngleCos: number;
  private readonly soundConeAngleCos: number;
  private readonly soundConeRangeSq: number;
  private readonly soundConeCenterCos: number;
  private readonly soundConeCenterSin: number;
  private readonly _soundConeCenterRad: number;

  // Detection results (updated each frame)
  private _hasLOS: boolean = false;
  private _detectedBySound: boolean = false;

  // Player tracking — updated every frame for movement detection
  private prevPlayerX: number = NaN;
  private prevPlayerY: number = NaN;
  private _playerDirX: number = 0;
  private _playerDirY: number = 0;

  private readonly groundLayer: Phaser.Tilemaps.TilemapLayer;

  constructor(config: EnemyArchetypeConfig, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.effectiveDetectionRadius = config.detectionRadiusMultiplier * DETECTION_RANGE;
    this.effectiveDetectionRadiusSq = this.effectiveDetectionRadius * this.effectiveDetectionRadius;
    this.visionAngleCos = Math.cos(degreesToRadians(config.visionAngle / 2));
    this.soundConeAngleCos = Math.cos(degreesToRadians(config.soundConeAngle / 2));
    this.soundConeRangeSq = config.soundConeRange * config.soundConeRange;
    // Sound cone center = vision half-angle + sound half-angle (inner edge meets vision cone)
    this._soundConeCenterRad = degreesToRadians(config.visionAngle / 2 + config.soundConeAngle / 2);
    this.soundConeCenterCos = Math.cos(this._soundConeCenterRad);
    this.soundConeCenterSin = Math.sin(this._soundConeCenterRad);
    this.groundLayer = groundLayer;
  }

  get hasLOS(): boolean { return this._hasLOS; }
  get detectedBySound(): boolean { return this._detectedBySound; }
  get playerDirX(): number { return this._playerDirX; }
  get playerDirY(): number { return this._playerDirY; }

  /** Sound cone half-angle in radians (for debug overlay). */
  get soundConeHalfAngleRad(): number { return Math.acos(this.soundConeAngleCos); }
  /** Vision cone half-angle in radians (for debug overlay). */
  get visionHalfAngleRad(): number { return Math.acos(this.visionAngleCos); }
  /** Sound cone center offset in radians (for debug overlay). */
  get soundConeCenterRad(): number { return this._soundConeCenterRad; }
  /** Sound cone range in pixels (for debug overlay). */
  get soundConeRangeVal(): number { return Math.sqrt(this.soundConeRangeSq); }

  /**
   * Run the full detection pipeline for one frame.
   * Updates hasLOS, detectedBySound, and player direction tracking.
   * Returns whether LOS changed this frame (for logging).
   */
  update(
    entityX: number, entityY: number,
    playerX: number, playerY: number,
    facingX: number, facingY: number,
    state: AIState,
    playerHideState?: PlayerHideState,
  ): { losChanged: boolean; prevLOS: boolean } {
    // Player movement check — sound cones only detect moving targets
    const playerIsMoving = !Number.isNaN(this.prevPlayerX) &&
      distanceSq(this.prevPlayerX, this.prevPlayerY, playerX, playerY) > 0.25;

    const prevLOS = this._hasLOS;
    const dx = playerX - entityX;
    const dy = playerY - entityY;
    const distSq = distanceSq(entityX, entityY, playerX, playerY);

    if (distSq <= this.effectiveDetectionRadiusSq) {
      // At point-blank range (within 3 tiles / 48px), skip Bresenham raycast —
      // wall geometry can't block LOS at melee range. Vision/sound cones still apply.
      const pointBlankRaycast = distSq < POINT_BLANK_RANGE_SQ;
      // At overlap distance (< 1px), bypass cone checks — can't miss someone you're overlapping.
      const pointBlank = distSq < 1;
      const useWidenedVision = state === 'search' || state === 'alert';
      const hasRaycast = pointBlankRaycast || this.checkLOS(entityX, entityY, playerX, playerY);
      const inVision = (pointBlank || this.isInVisionCone(dx, dy, distSq, facingX, facingY, useWidenedVision)) && hasRaycast;
      const inSound = !inVision && !pointBlank && playerIsMoving && !playerHideState?.suppressSoundDetection && this.isInSoundCone(dx, dy, distSq, facingX, facingY) && hasRaycast;
      let detected = inVision || inSound;

      // Hide suppression: if player is hidden, suppress detection unless proximity challenge fails
      if (detected && playerHideState?.isHidden) {
        const proxDist = playerHideState.proximityDistancePx;
        if (distSq <= proxDist * proxDist) {
          // Proximity range — challenge the hide
          detected = !playerHideState.proximityCheck();
        } else {
          // Out of proximity range — hidden player is invisible
          detected = false;
        }
      }

      this._hasLOS = detected;
      this._detectedBySound = inSound && !inVision && detected;
    } else {
      this._hasLOS = false;
      this._detectedBySound = false;
    }

    // Track player direction (only when we have LOS)
    if (this._hasLOS && !Number.isNaN(this.prevPlayerX)) {
      const pdx = playerX - this.prevPlayerX;
      const pdy = playerY - this.prevPlayerY;
      const pLen = magnitude(pdx, pdy);
      if (pLen > 0.5) {
        const pDir = normalize(pdx, pdy);
        this._playerDirX = pDir.x;
        this._playerDirY = pDir.y;
      }
    }

    // Always update prev position — needed for movement detection next frame
    this.prevPlayerX = playerX;
    this.prevPlayerY = playerY;

    return { losChanged: this._hasLOS !== prevLOS, prevLOS };
  }

  /** Bresenham LOS with diagonal corner check. */
  checkLOS(fromX: number, fromY: number, toX: number, toY: number): boolean {
    const tileFromX = worldToTile(fromX);
    const tileFromY = worldToTile(fromY);
    const tileToX = worldToTile(toX);
    const tileToY = worldToTile(toY);

    let x = tileFromX;
    let y = tileFromY;
    const absDx = Math.abs(tileToX - tileFromX);
    const absDy = Math.abs(tileToY - tileFromY);
    const sx = tileFromX < tileToX ? 1 : -1;
    const sy = tileFromY < tileToY ? 1 : -1;
    let err = absDx - absDy;

    while (true) {
      if (x !== tileFromX || y !== tileFromY) {
        const tile = this.groundLayer.getTileAt(x, y);
        if (tileBlocksLOS(tile)) return false;
      }

      if (x === tileToX && y === tileToY) break;

      const e2 = 2 * err;
      const stepX = e2 > -absDy;
      const stepY = e2 < absDx;

      if (stepX && stepY) {
        const adjX = this.groundLayer.getTileAt(x + sx, y);
        const adjY = this.groundLayer.getTileAt(x, y + sy);
        if (tileBlocksLOS(adjX) && tileBlocksLOS(adjY)) return false;
      }

      if (stepX) { err -= absDy; x += sx; }
      if (stepY) { err += absDx; y += sy; }
    }

    return true;
  }

  /**
   * Check whether this enemy can perceive an agitated ally at the given position.
   * Sound: in sound cone + in range (no LOS — shouting/fighting audible through walls).
   * Vision: in detection radius + in vision cone + LOS (need clear sight).
   */
  canPerceiveAlertedAlly(
    entityX: number, entityY: number,
    targetX: number, targetY: number,
    facingX: number, facingY: number,
    state: AIState,
  ): boolean {
    const dx = targetX - entityX;
    const dy = targetY - entityY;
    const distSq = distanceSq(entityX, entityY, targetX, targetY);

    // Sound path — no LOS required
    if (this.isInSoundCone(dx, dy, distSq, facingX, facingY)) return true;

    // Vision path — needs detection radius + cone + LOS
    if (distSq <= this.effectiveDetectionRadiusSq) {
      const useWidened = state === 'search' || state === 'alert';
      if (this.isInVisionCone(dx, dy, distSq, facingX, facingY, useWidened)) {
        if (this.checkLOS(entityX, entityY, targetX, targetY)) return true;
      }
    }

    return false;
  }

  private isInVisionCone(dx: number, dy: number, distSq: number, facingX: number, facingY: number, useWidened: boolean): boolean {
    if (distSq < 0.01) return true;
    const len = Math.sqrt(distSq);
    const dotProd = dot(facingX, facingY, dx / len, dy / len);
    const threshold = useWidened ? this.visionAngleCos * 0.5 : this.visionAngleCos;
    return dotProd >= threshold;
  }

  private isInSoundCone(dx: number, dy: number, distSq: number, facingX: number, facingY: number): boolean {
    if (distSq < 0.01) return true;
    if (distSq > this.soundConeRangeSq) return false;

    const len = Math.sqrt(distSq);
    const normDx = dx / len;
    const normDy = dy / len;

    // Left cone center: rotate facing by -centerAngle
    const leftCenterX = facingX * this.soundConeCenterCos + facingY * this.soundConeCenterSin;
    const leftCenterY = -facingX * this.soundConeCenterSin + facingY * this.soundConeCenterCos;
    if (dot(leftCenterX, leftCenterY, normDx, normDy) >= this.soundConeAngleCos) return true;

    // Right cone center: rotate facing by +centerAngle
    const rightCenterX = facingX * this.soundConeCenterCos - facingY * this.soundConeCenterSin;
    const rightCenterY = facingX * this.soundConeCenterSin + facingY * this.soundConeCenterCos;
    if (dot(rightCenterX, rightCenterY, normDx, normDy) >= this.soundConeAngleCos) return true;

    return false;
  }
}
